<template>
    <span
        v-if="items.length > 0"
        class="link-cell"
        :data-test-id="dataTestId"
    >
        <component
            :is="item.tag"
            v-for="(item, index) in items"
            :key="index"
            class="link-cell__link"
            :data-test-id="`${dataTestId}-item-${index}`"
            v-bind="item.attrs"
        >
            <SearchBadge
                :data-test-id="`${dataTestId}-item-${index}`"
                :icon="item.icon"
                :label="item.display_name"
            />
        </component>
    </span>
    <span v-else>
        {{ EMPTY_LABEL }}
    </span>
</template>

<script>
import EMPTY_LABEL from './empty-label'
import SearchBadge from './SearchBadge.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import searchResultActionTypes from '@/constants/search-result-action-types'

export default {
    name: 'LinkCell',

    components: {
        SearchBadge,
    },

    mixins: [needsOrganizationSlug],

    props: {
        dataTestId: {
            type: String,
            required: true,
        },
        // An array of serialized SearchResultActions
        // ({ type, display_name, icon, url|path, open_in_new_tab }).
        value: {
            type: Array,
            required: false,
            default: () => [],
        },
    },

    computed: {
        EMPTY_LABEL: () => EMPTY_LABEL,

        items() {
            return (this.value ?? []).map((item) => {
                const openInNewTab = !!item.open_in_new_tab
                // An external link or a new-tab link renders as an anchor;
                // an internal one renders as a router-link. Both are real
                // links, so cmd/right-click opens them in a new tab.
                const useAnchor = item.type === searchResultActionTypes.EXTERNAL_LINK || openInNewTab
                const url = this.makeUrl(item)

                return {
                    display_name: item.display_name,
                    icon: item.icon,
                    tag: useAnchor ? 'a' : 'router-link',
                    attrs: useAnchor ? { href: url, target: openInNewTab ? '_blank' : '_self' } : { to: url },
                }
            })
        },
    },

    methods: {
        makeUrl(item) {
            if (item.type === searchResultActionTypes.EXTERNAL_LINK) {
                return item.url
            }

            // A slugless page has no slug to prefix; interpolating an empty
            // one would produce a protocol-relative "//relays/1" rather
            // than a route.
            return this.organizationSlug ? `/${this.organizationSlug}${item.path}` : item.path
        },
    },
}
</script>

<style scoped lang="scss">
// Fill the table cell so the badge is bounded by the column width. The
// search table runs in fixed layout, so the column has a definite width;
// max-width: 100% keeps the badge within it and lets it grow as the column
// is widened, while the min-width: 0 threaded down every flex level (and on
// into SearchBadge) lets the label shrink below its natural width and
// truncate. Short labels still size to content.
.link-cell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
}

.link-cell__link {
    display: inline-flex;
    min-width: 0;
    max-width: 100%;
    text-decoration: none;
    cursor: pointer;
}
</style>
